/**
 * Centralized Configuration System
 * Reads from window._env_ (local-config.js or env-config.js). No hardcoded config file.
 */

import { authLogger } from './authLogger';
import logger from './logger';

class CentralizedConfig {
  constructor() {
    this.isLocalhost = this.detectLocalhost();
    this.enviroment = null;
    this.branch = null;
    this.config = null;
    this.initialized = false;
    
    // Initialize asynchronously
    this.initialize();
  }

  async initialize() {
    // Wait for local-config.js (localhost) or env-config.js (cloud) to load (window._env_ to be set)
    // Both are loaded asynchronously via script tags, so we need to wait for them
    const maxWait = 5000; // 5 seconds max wait
    const startTime = Date.now();
    const checkInterval = 50; // Check every 50ms
    
    while (!window._env_ && (Date.now() - startTime) < maxWait) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
    }
    
    if (!window._env_) {
      logger.warn('CONFIG', `${this.isLocalhost ? 'local-config.js' : 'env-config.js'} not loaded after 5 seconds; no fallbacks will be applied`);
      window._env_ = {};
    }
    
    this.enviroment = await this.getenviroment();
    this.branch = await this.getBranch();
    this.config = this.buildConfiguration();
    
    // Keep config logging minimal in production (verbose logging belongs behind DEBUG).
    
    // Set window._env_ for compatibility (merge with existing if present)
    if (!this.isLocalhost) {
      // For cloud: env-config.js must win so CUSTOMER_DOMAIN, REACT_APP_AUTH_PORTAL_URL, etc. from the pod are preserved.
      // Merge with this.config as base, then overlay window._env_ so env-config.js values take precedence.
      window._env_ = { ...this.config, ...(window._env_ || {}) };
      // When env-config.js has no CUSTOMER_DOMAIN (e.g. old image or ConfigMap not applied), derive from same
      // naming rules as backend (get_customer_portal_domain) so redirect works. Run when on cloud by hostname
      // or when USE_CLOUD_KUBERNETES is set (explicit cloud signal from pod).
      const onCloudByHost = typeof window !== 'undefined' && (window.location.hostname === 'b-esper.com' || window.location.hostname.endsWith('.b-esper.com'));
      const onCloudByEnv = (window._env_ && (window._env_.USE_CLOUD_KUBERNETES === true || window._env_.USE_CLOUD_KUBERNETES === 'true'));
      const v = window._env_;
      const customerDomainBefore = v.CUSTOMER_DOMAIN || v.customer_domain || '(missing)';
      // Use console so we don't deadlock (logger waits for config init)
      console.info('[CONFIG] Cloud init: isLocalhost=false, onCloudByHost=', onCloudByHost, ', onCloudByEnv=', onCloudByEnv, ', CUSTOMER_DOMAIN before=', customerDomainBefore);
      if (typeof window !== 'undefined' && (onCloudByHost || onCloudByEnv)) {
        const region = (v.REGION_CODE || '').trim();
        const provider = (v.PROVIDER_CODE || '').trim();
        const envName = (v.ENVIRONMENT || v.environment || v.enviroment || '').trim().toLowerCase();
        const branch = (v.BRANCH_CODE || v.branch_code || '').trim().toLowerCase();
        if (region && provider && (!v.CUSTOMER_DOMAIN || !String(v.CUSTOMER_DOMAIN).trim())) {
          const base = 'b-esper.com';
          const customerDomain = (envName === 'prod' && branch === 'main')
            ? `portal.${region}.${provider}.${base}`
            : `portal.${branch}.${envName}.${region}.${provider}.${base}`;
          v.CUSTOMER_DOMAIN = customerDomain;
          if (!v.REACT_APP_AUTH_PORTAL_URL || !String(v.REACT_APP_AUTH_PORTAL_URL).trim()) {
            v.REACT_APP_AUTH_PORTAL_URL = `https://${customerDomain}`;
          }
          console.info('[CONFIG] Derived CUSTOMER_DOMAIN (env-config had none):', customerDomain);
        } else {
          console.info('[CONFIG] CUSTOMER_DOMAIN not derived: region=', region, 'provider=', provider, 'hadDomain=', !!v.CUSTOMER_DOMAIN);
        }
      }
      console.info('[CONFIG] After merge: CUSTOMER_DOMAIN=', v.CUSTOMER_DOMAIN || '(still missing)');
    } else {
      // For localhost or if window._env_ not set, use our built config
      window._env_ = this.config;
    }
    
    this.initialized = true;
    
    // logConfiguration() intentionally not called automatically to avoid noisy logs.
  }

  detectLocalhost() {
    if (typeof window === 'undefined') {
      authLogger.debug('[Config] detectLocalhost: window is undefined (SSR), returning false');
      return false;
    }
    // Explicit cloud signal from env-config.js (set by K8s ConfigMap) – if present and true, we're on cloud
    const env = window._env_ || {};
    const useCloud = env.USE_CLOUD_KUBERNETES === true || env.USE_CLOUD_KUBERNETES === 'true';
    if (useCloud) {
      authLogger.debug('[Config] detectLocalhost: USE_CLOUD_KUBERNETES=true, isLocalhost=false');
      return false;
    }
    const hostname = window.location.hostname;
    if (hostname === 'b-esper.com' || hostname.endsWith('.b-esper.com')) {
      authLogger.debug(`[Config] detectLocalhost: hostname="${hostname}" is cloud (b-esper.com), isLocalhost=false`);
      return false;
    }
    const isLocal = hostname === 'localhost' ||
                    hostname === '127.0.0.1' ||
                    hostname.startsWith('192.168.') ||
                    hostname.startsWith('10.') ||
                    hostname.includes('local');
    authLogger.debug(`[Config] detectLocalhost: hostname="${hostname}", isLocalhost=${isLocal}`);
    return isLocal;
  }

  async getenviroment() {
    const envConfig = window._env_ || {};
    if (this.isLocalhost) {
      return envConfig.ENVIRONMENT || envConfig.environment || envConfig.enviroment || process.env.REACT_APP_enviroment || this.enviroment || '';
    }
    return envConfig.ENVIRONMENT || envConfig.environment || envConfig.enviroment || this.enviroment || '';
  }

  async getBranch() {
    const envConfig = window._env_ || {};
    if (this.isLocalhost) {
      return envConfig.BRANCH_CODE || process.env.REACT_APP_BRANCH || this.branch || '';
    }
    return envConfig.BRANCH_CODE || this.branch || '';
  }

  buildConfiguration() {
    // CRITICAL: Prioritize window._env_ (from env-config.js) over this.enviroment
    // window._env_ is set by env-config.js during deployment and has the correct values
    const envConfig = window._env_ || {};
    const env = envConfig.ENVIRONMENT || envConfig.environment || envConfig.enviroment || this.enviroment || '';
    const branch = envConfig.BRANCH_CODE || this.branch || '';
    const branchCode = branch;
    
    // Use local API URL when on localhost; cloud: K8s API domain or legacy backend URL.
    // API_BASE_URL from window._env_ (set by generate-local-config.js) takes precedence.
    let apiBaseUrlVal;
    const envApiUrl = envConfig.API_BASE_URL;
    const isBlobStorageUrl = envApiUrl && typeof envApiUrl === 'string' && envApiUrl.includes('blob.core.windows.net');
    if (envApiUrl && !isBlobStorageUrl) {
      apiBaseUrlVal = envApiUrl;
    } else if (isBlobStorageUrl) {
      logger.warn('CONFIG', 'API_BASE_URL is blob storage URL - ignoring', { rejected: envApiUrl });
      apiBaseUrlVal = null;
    }
    if (apiBaseUrlVal == null && !this.isLocalhost) {
      apiBaseUrlVal = undefined;
    }
    if (apiBaseUrlVal === undefined || apiBaseUrlVal == null) {
    if (this.isLocalhost) {
      apiBaseUrlVal = 'http://localhost:5100';
    } else {
      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      const isBesperDomain = hostname === 'b-esper.com' || (hostname && hostname.endsWith('.b-esper.com'));
      const isK8sDomain = isBesperDomain ||
        (hostname.includes('.b-esper.com') &&
          (hostname.includes(`.${env}.`) || hostname === 'b-esper.com' || hostname.includes(`.${branchCode}.`)));

      if (isK8sDomain && env && branchCode) {
        let regionCode = 'de';
        let providerCode = 'az';
        if (hostname === 'b-esper.com' || (env === 'prod' && branchCode === 'main')) {
          const prodPattern = /(?:portal|api|admin)\.([^.]+)\.([^.]+)\.b-esper\.com/;
          const prodMatch = hostname.match(prodPattern);
          if (prodMatch) {
            [, regionCode, providerCode] = prodMatch;
          }
          apiBaseUrlVal = `https://api.${regionCode}.${providerCode}.b-esper.com`;
          logger.debug('CONFIG', 'b-esper.com or prod/main: using K8s API endpoint', { hostname, regionCode, providerCode, apiBaseUrlVal });
        } else {
          const portalPattern = new RegExp(`portal\\.${branchCode}\\.${env}\\.([^.]+)\\.([^.]+)\\.b-esper\\.com`);
          let match = hostname.match(portalPattern);
          if (!match) {
            const apiPattern = new RegExp(`api\\.${branchCode}\\.${env}\\.([^.]+)\\.([^.]+)\\.b-esper\\.com`);
            match = hostname.match(apiPattern);
          }
          if (!match) {
            const adminPattern = new RegExp(`admin\\.${branchCode}\\.${env}\\.([^.]+)\\.([^.]+)\\.b-esper\\.com`);
            match = hostname.match(adminPattern);
          }
          if (match) {
            [, regionCode, providerCode] = match;
          }
          apiBaseUrlVal = `https://api.${branchCode}.${env}.${regionCode}.${providerCode}.b-esper.com`;
          logger.debug('CONFIG', 'K8s API endpoint', { hostname, env, branchCode, regionCode, providerCode, apiBaseUrlVal });
        }
      } else {
        apiBaseUrlVal = null;
        if (isBesperDomain) {
          logger.error('CONFIG', 'env-config.js missing or invalid on *.b-esper.com: ENVIRONMENT and BRANCH_CODE required. No API URL fallback.', { hostname, env: env || '(empty)', branchCode: branchCode || '(empty)' });
        } else {
          logger.error('CONFIG', 'Not on *.b-esper.com and no API_BASE_URL from env-config. No legacy fallback.', { hostname });
        }
      }
    }
    }
    const apiBaseUrl = apiBaseUrlVal;
    const apiInternalUrl = apiBaseUrlVal;
    const apiCustomerPortalUrl = apiBaseUrlVal;

    // Streaming gateway base URL (SSE for scrape progress)
    let streamingBaseUrlVal;
    if (this.isLocalhost) {
      streamingBaseUrlVal = 'http://localhost:4012';
    } else {
      const hostnameStream = typeof window !== 'undefined' ? window.location.hostname : '';
      const isBesperStream = hostnameStream === 'b-esper.com' || (hostnameStream && hostnameStream.endsWith('.b-esper.com'));
      const isK8sDomainStream = isBesperStream ||
        (hostnameStream.includes('.b-esper.com') &&
          (hostnameStream.includes(`.${env}.`) || hostnameStream === 'b-esper.com' || hostnameStream.includes(`.${branchCode}.`)));
      if (isK8sDomainStream && env && branchCode) {
        let regionCode = 'de';
        let providerCode = 'az';
        if (hostnameStream === 'b-esper.com' || (env === 'prod' && branchCode === 'main')) {
          const prodPattern = /(?:portal|api|admin)\.([^.]+)\.([^.]+)\.b-esper\.com/;
          const prodMatch = hostnameStream.match(prodPattern);
          if (prodMatch) {
            [, regionCode, providerCode] = prodMatch;
          }
          streamingBaseUrlVal = env === 'prod' && branchCode === 'main'
            ? `https://streaming.${regionCode}.${providerCode}.b-esper.com`
            : `https://streaming.${branchCode}.${env}.${regionCode}.${providerCode}.b-esper.com`;
        } else {
          const portalPattern = new RegExp(`portal\\.${branchCode}\\.${env}\\.([^.]+)\\.([^.]+)\\.b-esper\\.com`);
          let match = hostnameStream.match(portalPattern);
          if (!match) {
            const apiPattern = new RegExp(`api\\.${branchCode}\\.${env}\\.([^.]+)\\.([^.]+)\\.b-esper\\.com`);
            match = hostnameStream.match(apiPattern);
          }
          if (!match) {
            const adminPattern = new RegExp(`admin\\.${branchCode}\\.${env}\\.([^.]+)\\.([^.]+)\\.b-esper\\.com`);
            match = hostnameStream.match(adminPattern);
          }
          if (match) {
            [, regionCode, providerCode] = match;
          }
          streamingBaseUrlVal = `https://streaming.${branchCode}.${env}.${regionCode}.${providerCode}.b-esper.com`;
        }
      } else {
        streamingBaseUrlVal = envConfig.STREAMING_BASE_URL || null;
      }
    }
    
    // Construct everything from env and branch
    // TODO: config is app-specific, removed for platform-core
    const configuredFlowName = envConfig.ENTRA_USER_FLOW_NAME || 'b-esper_signup';
    // Authority URL should use tenant domain name (not tenant ID) for user flow endpoint
    // Format: https://{tenant-domain}/{tenant-domain-name} (e.g., besperexternaldev.onmicrosoft.com)
    // NOTE: The user flow name will be appended to this base authority URL in msalConfig.js
    // to ensure deterministic flow selection. Do NOT include the flow name in ENTRA_AUTHORITY_URL.
    const tenantDomainName = envConfig.ENTRA_TENANT_DOMAIN_NAME || (this.isLocalhost ? `besperexternal${env || 'dev'}.onmicrosoft.com` : null);
    
    let baseAuthorityUrl = envConfig.ENTRA_AUTHORITY_URL;
    if (!baseAuthorityUrl && (envConfig.ENTRA_TENANT_DOMAIN || envConfig.ENTRA_AUTHORITY_DOMAIN || envConfig.ENTRA_KNOWN_AUTHORITY) && tenantDomainName) {
      const authorityDomain = envConfig.ENTRA_TENANT_DOMAIN || envConfig.ENTRA_AUTHORITY_DOMAIN || envConfig.ENTRA_KNOWN_AUTHORITY;
      baseAuthorityUrl = `https://${authorityDomain}/${tenantDomainName}`;
    }
    if (!baseAuthorityUrl && this.isLocalhost) {
      baseAuthorityUrl = `https://besperexternal${env || 'dev'}.ciamlogin.com/${tenantDomainName || 'besperexternaldev.onmicrosoft.com'}`;
    }
    
    if (baseAuthorityUrl) {
      const tenantIdPattern = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (tenantIdPattern.test(baseAuthorityUrl)) {
        logger.warn('CONFIG', 'Authority URL uses tenant ID format, converting to tenant domain name format', { originalUrl: baseAuthorityUrl });
        baseAuthorityUrl = baseAuthorityUrl.replace(tenantIdPattern, `/${tenantDomainName || ''}`);
        authLogger.debug('[Config] Converted authority URL to:', baseAuthorityUrl);
      }
    }
    
    const normalizedAuthority = baseAuthorityUrl ? baseAuthorityUrl.replace(/\/+$/, '') : null;
    
    // Log the final authority URL for debugging
    authLogger.debug('🔧 [Config] Final authority URL:', normalizedAuthority);
    authLogger.debug('🔧 [Config] Authority URL source:', envConfig.ENTRA_AUTHORITY_URL ? 'window._env_' : 'constructed from authority domain');
    
    const streamingBaseUrl = envConfig.STREAMING_BASE_URL != null ? envConfig.STREAMING_BASE_URL : streamingBaseUrlVal;

    return {
      enviroment: env,
      BRANCH_CODE: branch,
      API_BASE_URL: apiBaseUrl,
      API_INTERNAL_URL: apiInternalUrl,
      API_CUSTOMER_PORTAL_URL: apiCustomerPortalUrl,
      STREAMING_BASE_URL: streamingBaseUrl,
      NPM_PACKAGE_NAME: this.getNpmPackageName(env, branchCode),
      NPM_TAG: this.getNpmTag(env),
      CDN_BASE_URL: 'https://unpkg.com',
      RESOURCE_ID: envConfig.RESOURCE_ID || null,
      // Auto-disable auth if client ID is missing (for localhost development)
      ENABLE_AUTH: (envConfig.ENABLE_AUTH === 'true' || envConfig.ENABLE_AUTH === true) && envConfig.ENTRA_CUSTOMER_CLIENT_ID ? 'true' : 'false',
      DEBUG: env === 'dev',
      IS_LOCALHOST: this.isLocalhost,
      USE_LOCAL_API: this.isLocalhost,
      CONFIG_SOURCE: this.isLocalhost ? 'local-config.js' : (envConfig.ENTRA_CUSTOMER_CLIENT_ID ? 'env-config.js (deployed)' : 'default'),
      // Entra External ID Authentication Configuration - prioritize deployed env-config.js values
      // For localhost, provide defaults that allow the app to start (auth will be disabled if client ID is missing)
      // Read from window._env_ which is set by local-config.js or env-config.js
      ENTRA_CUSTOMER_CLIENT_ID: envConfig.ENTRA_CUSTOMER_CLIENT_ID || null,
      ENTRA_USER_FLOW_NAME: envConfig.ENTRA_USER_FLOW_NAME || (this.isLocalhost ? configuredFlowName : null),
      ENTRA_AUTHORITY_URL: envConfig.ENTRA_AUTHORITY_URL || normalizedAuthority,
      ENTRA_KNOWN_AUTHORITY: envConfig.ENTRA_KNOWN_AUTHORITY || envConfig.ENTRA_TENANT_DOMAIN || envConfig.ENTRA_AUTHORITY_DOMAIN || (this.isLocalhost ? `besperexternal${env || 'dev'}.ciamlogin.com` : null),
      ENTRA_TENANT_ID: envConfig.ENTRA_TENANT_ID || null,
      ENTRA_AUTHORITY_DOMAIN: envConfig.ENTRA_AUTHORITY_DOMAIN || envConfig.ENTRA_TENANT_DOMAIN || (this.isLocalhost ? `besperexternal${env || 'dev'}.ciamlogin.com` : null),
      ENTRA_API_SCOPE: envConfig.ENTRA_API_SCOPE || (envConfig.ENTRA_CUSTOMER_CLIENT_ID ? `api://${envConfig.ENTRA_CUSTOMER_CLIENT_ID}/customer.read` : null),
      STRIPE_PUBLISHABLE_KEY: envConfig.STRIPE_PUBLISHABLE_KEY || null,
      ENABLE_WIDGET: envConfig.ENABLE_WIDGET !== undefined 
        ? envConfig.ENABLE_WIDGET === 'true' || envConfig.ENABLE_WIDGET === true
        : true
    };
  }

  getNpmPackageName(env, branchCode) {
    // Use the standard package name for all enviroments
    return 'besper-sdk';
  }

  getNpmTag(env) {
    if (env === 'prod') return 'latest';
    if (env === 'int') return 'int';
    return 'dev';
  }

  getApiEndpoint(type = 'public') {
    // Ensure config is built
    if (!this.config) {
      this.config = this.buildConfiguration();
    }
    
    const envConfig = window._env_ || {};
    const apiBaseUrl = this.config?.API_BASE_URL ?? envConfig.API_BASE_URL ?? (this.isLocalhost ? 'http://localhost:5100' : null);
    
    let url;
    switch (type) {
      case 'internal':
        url = this.config?.API_INTERNAL_URL || envConfig.API_INTERNAL_URL || apiBaseUrl;
        break;
      case 'customer-portal':
        url = this.config?.API_CUSTOMER_PORTAL_URL || envConfig.API_CUSTOMER_PORTAL_URL || apiBaseUrl;
        break;
      case 'public':
      default:
        url = this.config?.API_BASE_URL || envConfig.API_BASE_URL || apiBaseUrl;
        break;
    }

    if ((url === undefined || url === null || url === '') && this.isLocalhost) {
      url = 'http://localhost:5100';
    }
    
    if (!url) {
      logger.error('CONFIG', `getApiEndpoint(${type}): URL is null/undefined`);
      return url;
    }

    // CRITICAL: SDK and API must always use the API endpoint, never blob storage.
    // If env-config.js or any source set API_BASE_URL to blob, reject it and use built config.
    const isBlobUrl = typeof url === 'string' && url.includes('blob.core.windows.net');
    if (isBlobUrl) {
      logger.warn('CONFIG', `getApiEndpoint(${type}): rejected blob storage URL, using API endpoint only`, { rejected: url });
      const safeUrl = type === 'internal'
        ? this.config?.API_INTERNAL_URL
        : type === 'customer-portal'
          ? this.config?.API_CUSTOMER_PORTAL_URL
          : this.config?.API_BASE_URL;
      url = safeUrl ?? (this.isLocalhost ? 'http://localhost:5100' : null);
      if (url && typeof url === 'string' && url.includes('blob.core.windows.net')) {
        logger.error('CONFIG', `getApiEndpoint(${type}): built config also has blob; this should not happen`);
        url = this.isLocalhost ? 'http://localhost:5100' : null;
      }
    }

    // Never use current origin as API when on public/portal/admin *.b-esper.com (avoids 405 from nginx).
    if (typeof window !== 'undefined' && url && !this.isLocalhost) {
      const origin = window.location.origin;
      const urlWithoutApi = typeof url === 'string' ? url.replace(/\/api\/?$/, '') : '';
      const isSameAsOrigin = (url === origin || urlWithoutApi === origin);
      const hostname = window.location.hostname;
      const isPublicDomain = hostname.includes('.b-esper.com') && !hostname.startsWith('api.');
      if (isSameAsOrigin && isPublicDomain) {
        const env = envConfig.ENVIRONMENT || envConfig.environment || envConfig.enviroment || this.enviroment || '';
        const branchCode = envConfig.BRANCH_CODE || this.branch || '';
        if (env && branchCode) {
          const regionCode = envConfig.REGION_CODE || 'de';
          const providerCode = envConfig.PROVIDER_CODE || 'az';
          const apiDomain = (env === 'prod' && branchCode === 'main')
            ? `https://api.${regionCode}.${providerCode}.b-esper.com`
            : `https://api.${branchCode}.${env}.${regionCode}.${providerCode}.b-esper.com`;
          logger.warn('CONFIG', `getApiEndpoint(${type}): API URL was same as origin on public domain; overrode to API domain`, { origin, apiDomain });
          url = apiDomain;
        }
        // If env/branch missing, do not substitute; url stays as-is (or null) and callers will fail
      }
    }

    logger.debug('CONFIG', `getApiEndpoint(${type})`, { baseUrl: url, isLocalhost: this.isLocalhost });
    
    // For cloud API URLs, add /api prefix if missing. Localhost URLs are returned as-is.
    const isLocalhostUrl = url && (url.includes('localhost') || url.includes('127.0.0.1'));
    const hasApiPrefix = url && (url.endsWith('/api') || url.match(/\/api\/|\/api$/));
    
    if (!isLocalhostUrl && url && !hasApiPrefix) {
      url = `${url}/api`;
      logger.debug('CONFIG', 'Added /api prefix for API URL', { url });
    } else if (isLocalhostUrl) {
      logger.debug('CONFIG', 'Localhost URL detected, returning as-is (calling code will add /api)', { url });
    } else if (hasApiPrefix) {
      logger.debug('CONFIG', 'URL already has /api prefix', { url });
    } else {
      logger.warn('CONFIG', 'Unexpected URL format', { url });
    }
    
    logger.debug('CONFIG', `getApiEndpoint(${type}) returning`, { url });
    return url;
  }

  getStreamingBaseUrl() {
    if (!this.config) {
      this.config = this.buildConfiguration();
    }
    const envConfig = window._env_ || {};
    const url = this.config?.STREAMING_BASE_URL ?? envConfig.STREAMING_BASE_URL ?? (this.isLocalhost ? 'http://localhost:4012' : null);
    return url || null;
  }

  getNpmCdnUrl() {
    // TODO: config is app-specific, removed for platform-core
    const env = this.enviroment || '';
    const branchCode = this.branch || '';
    return `https://unpkg.com/${this.getNpmPackageName(env, branchCode)}@${this.getNpmTag(env)}`;
  }

  logConfiguration() {
    // TODO: config is app-specific, removed for platform-core
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'N/A (SSR)';
    const env = this.enviroment || '';
    const branch = this.branch || '';
    const config = this.config || {};
    const logData = {
      hostname: hostname,
      enviroment: env,
      branch: branch,
      isLocalhost: this.isLocalhost,
      apiBaseUrl: config?.API_BASE_URL ?? '(not set)',
      apiCustomerPortalUrl: config?.API_CUSTOMER_PORTAL_URL ?? '(not set)',
      npmPackageName: this.getNpmPackageName(env, branch),
      hasClientId: !!config?.ENTRA_CUSTOMER_CLIENT_ID,
      hasAuthority: !!config?.ENTRA_AUTHORITY_DOMAIN,
      authEnabled: config?.ENABLE_AUTH === 'true',
      configSource: this.isLocalhost ? 'localhost' : (config?.API_BASE_URL ? 'env-config' : 'missing'),
      useLocalApi: this.isLocalhost
    };

    if (this.isLocalhost) {
      logger.debug('CONFIG', 'Localhost detected - using local API', logData);
    } else {
      logger.debug('CONFIG', 'Cloud detected - using API directly', logData);
    }
  }

  async waitForInitialization() {
    while (!this.initialized) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }
    return this;
  }

  get(key, defaultValue = null) {
    return this.config?.[key] || defaultValue;
  }

  getAll() {
    return { ...this.config };
  }

  isRunningLocally() {
    return this.isLocalhost;
  }

  getenviromentInfo() {
    // TODO: config is app-specific, removed for platform-core
    return {
      enviroment: this.enviroment || '',
      branch: this.branch || '',
      isLocalhost: this.isLocalhost
    };
  }
}

// Create singleton instance
export const centralizedConfig = new CentralizedConfig();

// Export convenience functions
export const getConfig = async (key, defaultValue = null) => {
  await centralizedConfig.waitForInitialization();
  return centralizedConfig.get(key, defaultValue);
};
export const getAllConfig = async () => {
  await centralizedConfig.waitForInitialization();
  return centralizedConfig.getAll();
};
export const isLocalenviroment = () => centralizedConfig.isRunningLocally();
export const getenviromentInfo = async () => {
  await centralizedConfig.waitForInitialization();
  return centralizedConfig.getenviromentInfo();
};

// Export specific endpoint getters for convenience
export const getApiEndpoint = async (type = 'public') => {
  await centralizedConfig.waitForInitialization();
  return centralizedConfig.getApiEndpoint(type);
};
export const getNpmPackageName = async () => {
  await centralizedConfig.waitForInitialization();
  return centralizedConfig.getNpmPackageName(centralizedConfig.enviroment, centralizedConfig.branch);
};
export const getNpmCdnUrl = async () => {
  await centralizedConfig.waitForInitialization();
  return centralizedConfig.getNpmCdnUrl();
};

// Export for backward compatibility
export default centralizedConfig;
